import { useMemo, useRef, useState } from "react";
import { Scheduler } from "@aldabil/react-scheduler";
import dayjs from "dayjs";
import { pl } from "date-fns/locale";
import { useCalendarIssues } from "components/CalendarIssues/context";
import calendar_translations from "helpers/calendar_translations";
import getIssueColorByStatus from "helpers/getIssueColorByStatus";

import WeekHeader from "./components/WeekHeader";
import IssueTitle from "./components/IssueTitle";
import TaskTitle from "./components/TaskTitle";
import EventItem from "./components/EventItem";
import EditModal from "./components/EditModal";
import CustomEditor from "./components/CustomEditor";
import IssueMultiple from "./components/IssueMultiple";

const format = "YYYY-MM-DD HH:mm";

const WeekScheduler = () => {
  const {
    is_editable,
    view_type,
    show_night_hours,
    holidays,
    events,
    tasks,
    calendar_date,
    setCalendarDate,
    setDraggableEvent,
    onConfirm,
  } = useCalendarIssues();
  const scheduler_ref = useRef();
  const [edited, setEdited] = useState(null);
  const [editorState, setEditorState] = useState(null);

  const openCustomEditor = (date) => {
    setEditorState({ date });
  };

  const handleDrag = (_, date, event) => setDraggableEvent({ date, event });

  const summary = useMemo(() => {
    const grouped = {};

    // Tasks
    tasks
      .filter(({ date }) => {
        if (view_type === "day") return false;
        if (show_night_hours) return true;
        const hour = dayjs(date).hour();
        return hour >= 6 && hour < 22;
      })
      .forEach((task) => {
        const key = dayjs(task.date).format(format);
        const start = dayjs(task.date).toDate();
        const end = dayjs(task.date).toDate();

        if (!grouped[key]) {
          grouped[key] = {
            event_id: key,
            start,
            end,
            issues: [],
            tasks: [],
          };
        }

        grouped[key].tasks = [...grouped[key].tasks, { ...task, type: "task" }];
      });

    // Events (issues)
    events
      .filter(({ service_date }) => {
        if (show_night_hours) return true;
        const hour = dayjs(service_date).hour();
        return hour >= 6 && hour < 22;
      })
      .forEach((issue) => {
        const key = dayjs(issue.service_date).format(format);
        const start = dayjs(issue.service_date).toDate();
        const end = dayjs(issue.service_date).toDate();

        if (!grouped[key]) {
          grouped[key] = {
            event_id: key,
            start,
            end,
            issues: [],
            tasks: [],
          };
        }

        grouped[key].issues = [
          ...grouped[key].issues,
          {
            ...issue,
            color: getIssueColorByStatus(issue.status),
            type: "issue",
          },
        ];
      });

    return Object.values(grouped).sort(
      (a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf()
    );
  }, [tasks, show_night_hours, events, view_type]);

  const data = useMemo(() => {
    return summary.map(({ ...item }) => ({
      ...item,
      type:
        item.issues.length + item.tasks.length > 1
          ? "multiple"
          : item.issues.length > 0
          ? "issue"
          : "task",
    }));
  }, [summary]);

  const week_name = useMemo(() => {
    const start_week_name = calendar_date
      ? dayjs(calendar_date).startOf("week").format("DD MMMM")
      : "-";
    const end_week_name = calendar_date
      ? dayjs(calendar_date).endOf("week").format("DD MMMM YYYY")
      : "-";

    return `${start_week_name} - ${end_week_name}`;
  }, [calendar_date]);

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `:root {
            --week-value: "${week_name}";
          }`,
        }}
      />
      {editorState && (
        <CustomEditor
          date={editorState.date}
          close={() => setEditorState(null)}
          handleEdit={(type) => {
            setEditorState(null);
            setEdited({
              event: null,
              type,
              date: editorState.date,
            });
          }}
        />
      )}
      <div className={`day-calendar-modal calendar-type-${view_type}`}>
        <Scheduler
          ref={scheduler_ref}
          key={[view_type, calendar_date]}
          agenda={false}
          deletable={false}
          draggable={is_editable}
          resourceViewMode="tabs"
          customViewer={(event, close) =>
            event.type === "multiple" ? (
              <IssueMultiple
                {...event}
                close={close}
                onEdit={(item, type) => {
                  close();
                  setEdited({
                    event: item,
                    type,
                    date: null,
                  });
                }}
              />
            ) : event.type === "issue" ? (
              <IssueTitle {...event} close={close} />
            ) : (
              <TaskTitle
                {...event}
                close={close}
                onEdit={(item) => {
                  close();
                  setEdited({
                    event: item,
                    type: "edit_task",
                    date: null,
                  });
                }}
              />
            )
          }
          selectedDate={calendar_date}
          onSelectedDateChange={(date) => setCalendarDate(date)}
          locale={pl}
          view={view_type}
          events={data}
          hourFormat={24}
          disableViewNavigator={true}
          translations={calendar_translations}
          eventRenderer={({ event, ...actions }) => (
            <EventItem
              item={event}
              actions={actions}
              openCustomEditor={openCustomEditor}
            />
          )}
          editable={false}
          customEditor={() => null}
          onEventDrop={handleDrag}
          day={{
            startHour: show_night_hours ? 0 : 6,
            endHour: show_night_hours ? 24 : 22,
            step: 15,
            headRenderer: (date) => <WeekHeader date={date} />,
            cellRenderer: ({ start, end, day, ...rest }) => {
              const week_day = dayjs(start).format("dd");
              const is_weekend = ["So", "Nd"].includes(week_day);
              const day_holidays = holidays.filter(
                ({ date }) => date === dayjs(start).format("YYYY-MM-DD")
              );
              const is_holiday = day_holidays?.length > 0;

              return (
                <button
                  {...rest}
                  onClick={() => openCustomEditor(start)}
                  className={`rs__cell__button ${is_holiday ? " holiday" : ""}${
                    is_weekend ? " weekend" : ""
                  }`}
                  tabindex="0"
                  type="button"
                >
                  <span />
                </button>
              );
            },
          }}
          week={{
            startHour: show_night_hours ? 0 : 6,
            endHour: show_night_hours ? 24 : 22,
            step: 15,
            weekStartOn: 1,
            headRenderer: (date) => (
              <WeekHeader date={date} holidays={holidays} />
            ),
            disableGoToDay: true,
            cellRenderer: ({ start, end, day, ...rest }) => {
              const week_day = dayjs(start).format("dd");
              const is_weekend = ["So", "Nd"].includes(week_day);
              const day_holidays = holidays.filter(
                ({ date }) => date === dayjs(start).format("YYYY-MM-DD")
              );
              const is_holiday = day_holidays?.length > 0;

              return (
                <button
                  {...rest}
                  onClick={() => openCustomEditor(start)}
                  className={`rs__cell__button ${is_holiday ? " holiday" : ""}${
                    is_weekend ? " weekend" : ""
                  }`}
                  tabindex="0"
                  type="button"
                >
                  <span />
                </button>
              );
            },
          }}
        />
        {edited?.type && (
          <EditModal
            type={edited.type}
            handleConfirm={onConfirm}
            date={edited.date}
            close={() => setEdited(null)}
            item={edited.event}
          />
        )}
      </div>
    </>
  );
};

export default WeekScheduler;
